import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { mangas } from '@/mocks/manga';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = mangas.find((m) => m.slug === slug);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">
              漫画未找到
            </h1>
            <p className="text-foreground-500 mb-6">抱歉，未找到该漫画作品</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              <span>返回首页</span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const sortedChapters = [...manga.chapters].sort((a, b) =>
    sortOrder === 'asc' ? a.number - b.number : b.number - a.number
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 刃牙漫画在线观看`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: manga.updateDate,
    image: manga.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '刃牙漫画在线观看',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baki-manga.com' },
      { '@type': 'ListItem', position: 2, name: manga.category, item: `https://baki-manga.com/category/${manga.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: manga.title, item: `https://baki-manga.com/article/${manga.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${manga.title} - 刃牙漫画${selectedChapter ? ` 第${selectedChapter}话` : ''}`}
        description={manga.description}
        canonicalUrl={`https://baki-manga.com/article/${manga.slug}`}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: manga.category, to: `/category/${manga.categorySlug}` },
                { label: manga.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    title={`${manga.title} - 刃牙漫画在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    manga.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-background-200 text-foreground-600'
                  }`}>
                    {manga.status}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {manga.category}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {manga.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {manga.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {manga.views > 1000000
                      ? `${(manga.views / 10000).toFixed(0)}万阅读`
                      : `${manga.views}阅读`}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-line"></i>
                    共 {manga.totalChapters} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed">
                  {manga.description}
                </p>

                <Link
                  to={`/article/${manga.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedChapter(sortedChapters[0].number);
                  }}
                  className="inline-flex items-center gap-2 mt-6 px-8 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-200"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  <span>开始阅读第一话</span>
                </Link>
              </div>
            </div>

            {selectedChapter && (
              <div className="mb-10 bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold font-heading text-foreground-950">
                    第 {selectedChapter} 话
                  </h2>
                  <button
                    onClick={() => setSelectedChapter(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200 text-foreground-500 transition-colors"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <div className="bg-background-50 rounded-lg p-6 md:p-10 min-h-[500px] flex flex-col items-center justify-center border border-background-200/70">
                  <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-book-read-line text-2xl text-primary-600"></i>
                  </div>
                  <p className="text-foreground-700 text-sm mb-1 font-medium">
                    {manga.chapters.find((c) => c.number === selectedChapter)?.title || `第${selectedChapter}话`}
                  </p>
                  <p className="text-foreground-400 text-xs mb-6">
                    下拉式阅读体验 · 共{manga.chapters.find((c) => c.number === selectedChapter)?.pages || '?'}页
                  </p>

                  <div className="flex items-center gap-3">
                    <button
                      disabled={selectedChapter <= 1}
                      onClick={() => setSelectedChapter(selectedChapter - 1)}
                      className="px-4 py-2 rounded-md bg-background-200 text-foreground-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-300 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>上一话
                    </button>
                    <span className="text-xs text-foreground-500">
                      {selectedChapter} / {manga.totalChapters}
                    </span>
                    <button
                      disabled={selectedChapter >= manga.totalChapters}
                      onClick={() => setSelectedChapter(selectedChapter + 1)}
                      className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      下一话<i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-16">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold font-heading text-foreground-950">
                  章节列表
                </h2>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-500 transition-colors"
                >
                  <i className={`text-sm ${sortOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>
                  {sortOrder === 'asc' ? '正序' : '倒序'}
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {sortedChapters.map((chapter) => (
                  <button
                    key={chapter.slug}
                    onClick={() => setSelectedChapter(chapter.number)}
                    className={`text-left p-3 rounded-lg border text-sm transition-all duration-200 ${
                      selectedChapter === chapter.number
                        ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-200 hover:bg-background-100'
                    }`}
                  >
                    <div className="font-medium text-xs truncate">{chapter.title}</div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-foreground-400">{chapter.pages}页</span>
                      <span className="text-[10px] text-foreground-400">{chapter.updateDate}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-16 bg-background-100 rounded-lg p-6 border border-background-200/70">
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mangas
                  .filter((m) => m.slug !== manga.slug && (m.categorySlug === manga.categorySlug || m.tags.some((t) => manga.tags.includes(t))))
                  .slice(0, 6)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2 bg-background-200">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          title={`${related.title} - 刃牙漫画在线观看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="text-xs font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-[10px] text-foreground-500">{related.rating}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}